import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink, Search, ChevronDown, Check, Pin, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'https://api.orbit-technology.com/api';

/**
 * The Zoho record behind an Orbit deal, one click from wherever the deal is.
 *
 * Two calls behind this, deliberately split: GET /zoho-crm/link/:id answers
 * from what is already stored on the client and never touches Zoho, so this can
 * render on every client screen without a CRM search per view. POST goes and
 * looks, and the backend keeps what it finds - which is the part that was
 * missing before, when every click re-ran the same search and threw the answer
 * away.
 *
 * When the match is not obvious the panel shows what else came back with the
 * reason each one matched, and picking one pins it. A pinned link outranks the
 * search everywhere in Orbit that resolves a record (Auto Submit's Associated
 * Link, the lender subform writes, the Ping Matt payload), so a correction only
 * has to be made once.
 */
export default function ZohoLink({ clientId, client, onSaved, compact = false }) {
  const [link, setLink]         = useState({ url: '', source: '', confidence: '', matchedOn: '', accountName: '' });
  const [candidates, setCands]  = useState([]);
  const [busy, setBusy]         = useState(false);
  const [open, setOpen]         = useState(false);
  const [msg, setMsg]           = useState('');
  const [pinInput, setPinInput] = useState('');

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('mca_token')}`,
  }), []);

  // What we already know, without a Zoho call.
  useEffect(() => {
    if (!clientId) return;
    let live = true;
    (async () => {
      try {
        const res = await fetch(`${API}/zoho-crm/link/${clientId}`, { headers: headers() });
        if (!res.ok || !live) return;
        const d = await res.json();
        if (live && d.url) setLink(d);
      } catch { /* the button still offers to go and look */ }
    })();
    return () => { live = false; };
  }, [clientId, headers]);

  const resolve = async (body = {}) => {
    if (!clientId) return;
    setBusy(true); setMsg('');
    try {
      const res = await fetch(`${API}/zoho-crm/link/${clientId}`, {
        method: 'POST', headers: headers(), body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      setCands(d.candidates || []);
      if (!d.url) {
        setMsg('No matching Zoho record found for this client.');
        setOpen(true);
        return null;
      }
      setLink(d);
      // A low-confidence hit is a guess: show it and what else matched rather
      // than opening a tab on a record that may not be the deal.
      if (d.confidence === 'low') {
        setMsg('Closest match only - check it is the right record before using it.');
        setOpen(true);
      }
      if (d.saved && onSaved) onSaved({ zohoUrl: d.url, ...(d.module === 'Accounts' && d.id ? { zohoId: d.id } : {}) });
      if (body.url && onSaved) onSaved({ zohoUrlOverride: d.url, zohoUrl: d.url, ...(d.module === 'Accounts' && d.id ? { zohoId: d.id } : {}) });
      return d;
    } catch (e) {
      setMsg(`Zoho lookup failed: ${e.message}`);
      setOpen(true);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const openRecord = (url) => window.open(url, '_blank', 'noopener');

  const primary = async () => {
    if (link.url) { openRecord(link.url); return; }
    const d = await resolve();
    if (d?.url && d.confidence !== 'low') openRecord(d.url);
  };

  const pin = async (url) => {
    const d = await resolve({ url });
    if (d?.url) { setOpen(false); setPinInput(''); openRecord(d.url); }
  };

  const CONF_LABEL = {
    pinned: 'set by hand',
    high:   'strong match',
    medium: 'likely match',
    low:    'closest match',
    saved:  'saved earlier',
  };

  return (
    <div className="relative inline-flex items-center">
      <div className="inline-flex items-center">
        <button
          type="button"
          onClick={primary}
          disabled={busy || !clientId}
          title={link.url
            ? `Open this deal in Zoho${link.matchedOn ? ` - matched on ${link.matchedOn}` : ''}`
            : 'Search Zoho for this deal and keep the link'}
          // One look for both states - only the icon and wording say whether
          // the deal is linked (Open in Zoho) or still to be found (Find in
          // Zoho). The solid orange "linked" version stood out against the
          // outlined buttons beside it.
          className={`inline-flex items-center gap-1.5 ${compact ? 'text-[11px] px-2 py-1' : 'text-xs px-3 py-1.5'} rounded-l-lg font-medium transition-colors disabled:opacity-50 bg-white border border-orange-300 text-orange-700 hover:bg-orange-50`}
        >
          {busy
            ? <RefreshCw size={12} className="animate-spin" />
            : link.url ? <ExternalLink size={12} /> : <Search size={12} />}
          {busy ? 'Looking up…' : link.url ? 'Open in Zoho' : 'Find in Zoho'}
        </button>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          title="Zoho match details"
          className={`inline-flex items-center ${compact ? 'px-1 py-1' : 'px-1.5 py-1.5'} rounded-r-lg border border-l-0 transition-colors bg-white border-orange-300 text-orange-700 hover:bg-orange-50`}
        >
          <ChevronDown size={12} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
        </button>
      </div>

      {/* The match details (record, how confident, what it matched on) live in
          the dropdown and the tooltip. They used to sit as a caption under the
          button, which pushed it out of line with the status beside it. */}

      {open && (
        <div className="absolute z-30 top-full left-0 mt-1.5 w-80 bg-white border border-gray-200 rounded-xl shadow-lg p-3 space-y-2.5">
          {msg && <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">{msg}</p>}

          {link.url ? (
            <div className="text-[11px] text-gray-600 space-y-0.5">
              <p className="font-medium text-gray-800">{link.accountName || 'Linked record'}</p>
              <p className="truncate">{link.module || 'Accounts'} · {CONF_LABEL[link.confidence] || 'linked'}</p>
              {link.matchedOn && <p className="text-gray-400">matched on {link.matchedOn}</p>}
              {link.linkedBy && <p className="text-gray-400">linked by {link.linkedBy}</p>}
            </div>
          ) : (
            <p className="text-[11px] text-gray-500">No Zoho record linked to this deal yet.</p>
          )}

          <button
            type="button"
            onClick={() => resolve({ force: true })}
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 px-2 py-1.5 rounded-lg font-medium disabled:opacity-50"
          >
            <RefreshCw size={11} className={busy ? 'animate-spin' : ''} /> Search Zoho again
          </button>

          {candidates.length > 1 && (
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Other records found</p>
              {candidates.map(c => (
                <div key={`${c.module}-${c.id}`} className="flex items-start gap-2 border border-gray-100 rounded-lg px-2 py-1.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-medium text-gray-800 truncate">{c.name || c.contactName || c.id}</p>
                    <p className="text-[10px] text-gray-400 truncate">
                      {c.module}{c.matchedOn ? ` · ${c.matchedOn}` : ''}{c.email ? ` · ${c.email}` : ''}
                    </p>
                  </div>
                  <a href={c.url} target="_blank" rel="noopener noreferrer"
                    className="text-[10px] text-orange-600 hover:underline shrink-0 pt-0.5">open</a>
                  <button type="button" onClick={() => pin(c.url)} disabled={busy}
                    title="Use this record for this deal from now on"
                    className="inline-flex items-center gap-1 text-[10px] bg-orange-50 hover:bg-orange-100 border border-orange-200 text-orange-700 px-1.5 py-0.5 rounded shrink-0 disabled:opacity-50">
                    <Check size={10} /> use
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* The escape hatch: when the search cannot find it, paste the record
              from the address bar and it is pinned for everyone. */}
          <div className="pt-1 border-t border-gray-100 space-y-1.5">
            <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Or paste the Zoho URL</p>
            <div className="flex gap-1.5">
              <input
                value={pinInput}
                onChange={e => setPinInput(e.target.value)}
                placeholder="https://crm.zoho.com/.../tab/Accounts/123…"
                className="flex-1 min-w-0 bg-gray-50 border border-gray-200 text-gray-900 text-[11px] rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
              />
              <button
                type="button"
                onClick={() => pin(pinInput.trim())}
                disabled={busy || !pinInput.trim()}
                className="inline-flex items-center gap-1 text-[11px] bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white px-2.5 py-1.5 rounded-lg font-medium"
              >
                <Pin size={11} /> Pin
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
